import { useState, useEffect, useRef } from 'react';
import { requestSearchSummary } from '../../state/ducks/items/api';
import {
  Button,
  Form,
  Overlay,
  Popover,
  Spinner,
} from 'react-bootstrap';
import {
  setSearchFilter,
  setSearchQuery,
  setSearchStatus,
} from '../../state/ducks/search/actions';
import './SearchBox.scss';
import { useDispatch, useSelector } from 'react-redux';
import { FaSearch, FaSpinner } from 'react-icons/fa';
import { useDebounce } from 'use-debounce';
import { useNavigate, useLocation } from 'react-router-dom';

export default ({
  className = '',
}) => {

  const [show, setShow] = useState(false);
  const [fetchingSummary, setFetchingSummary] = useState(false);
  const searchFilters = useSelector((state) => state.search.filters);
  const [query, setQuery] = useState("");
  const [debouncedQuery] = useDebounce(query, 500);
  const target = useRef(null);
  const dispatch = useDispatch();
  const searchSummary = useSelector((state) => state.searchSummary);
  const history = useNavigate();
  const location = useLocation();
  const searchStatus = useSelector((state) => state.search?.status);

  const applySetSearchQuery = (event, value) => {
    event.preventDefault();
    dispatch(setSearchQuery(value));
    dispatch(setSearchStatus('running'));
    setShow(false);
    setQuery("");
    setFetchingSummary(false);
    if (location.pathname === '/') {
      let updatedFilters = { ...searchFilters };
      const filters = Object.keys(updatedFilters).reduce((acc, curr) => {
        const values = (updatedFilters[curr] || []).map((option) => option.value);
        if (values.length) acc[curr] = values;
        return acc;
      }, {});

      history({
        pathname: '/results',
        search: new URLSearchParams({ query, ...filters, sort:'relevancy' }).toString(),
      });
    }

    return false;
  }

  const getSearchSummary = (value) => {
    setShow(true);
    setFetchingSummary(true);
    dispatch(requestSearchSummary(value));
    setFetchingSummary(false);    
  };

  const applySearchSummaryFilter = (detail) => {
    dispatch(setSearchStatus('running'));
    let updatedFilters = { ...searchFilters };

    if (detail.fieldName === 'Product Name') {
      updatedFilters.itemNames = [...searchFilters.itemNames ?? [], { label: detail.foundTerm, value: detail.foundTerm}];
      dispatch(setSearchFilter('itemNames', updatedFilters.itemNames));
    }
    if (detail.fieldName === 'Product Category') {
      updatedFilters.itemCategories = [...searchFilters.itemCategories ?? [], { label: detail.foundTerm, value: detail.foundTerm}];
      dispatch(setSearchFilter('itemCategories', updatedFilters.itemCategories));
    }
    if (detail.fieldName === 'License Number') {
      updatedFilters.licenseNumbers = [...searchFilters.licenseNumbers ?? [], { label: detail.foundTerm, value: detail.foundTerm}];
      dispatch(setSearchFilter('licenseNumbers', updatedFilters.licenseNumbers));
    }

    const filters = Object.keys(updatedFilters).reduce((acc, curr) => {
      const values = (updatedFilters[curr] || []).map((option) => option.value);
      if (values.length) acc[curr] = values;
      return acc;
    }, {});

    setShow(false);
    setQuery("");
    setFetchingSummary(false);
    
    history({
      pathname: '/results',
      search: new URLSearchParams({ ...filters, sort:'relevancy' }).toString(),
    });
  }

  useEffect(() => {
    if (query.trim().length === 0) {
      setShow(false);
      setFetchingSummary(false);
      return;
    }
    setShow(true);
    setFetchingSummary(true);
    if (debouncedQuery) {
      getSearchSummary(debouncedQuery);
    }
  }, [debouncedQuery]);

  return (
    <div className="search-box">
      <Form.Group className="search-group" ref={target}>
        {searchStatus === 'running' && (
          <FaSpinner fontSize={14} className="searchSpinner" />
        )}
        {searchStatus !== 'running' && (
          <FaSearch fontSize={14} />
        )}
        <Form onSubmit={(e) => applySetSearchQuery(e, query)}>
          <Form.Control
            placeholder="Search the Metrc Catalog"
            className="search border-1"
            value={query}
            onChange={e => {setShow(true); setFetchingSummary(true); setQuery(e.target.value);}}
          />
        </Form>
      </Form.Group>
      <Overlay target={target} show={show} placement='bottom'>
        {(props) => (
          <Popover className={`search-box-popover ${className}`} {...props}>
            <Popover.Header as="h3" className="d-flex align-items-center">
              <FaSearch className="me-2 text-primary" />
              <span>Quick Filters</span>
            </Popover.Header>
            <Popover.Body>
              {fetchingSummary && (
                <div className="text-center mb-2">
                  <Spinner animation="border" size="sm" />
                </div>
              )}
              {!fetchingSummary && (
                <div className="search-summary-section mb-2">
                  <div className="d-flex justify-content-between align-items-center">
                    <span>
                      <span className="text-muted">Search Name, Category, License for:</span>
                      <span className="fw-bold ms-1">{query}</span>
                    </span>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      className="ms-2"
                      onClick={(e) => applySetSearchQuery(e, query)}
                    >
                      <FaSearch className="me-1 mt-n2" />Search ↵
                    </Button>
                  </div>
                </div>
              )}
              {!fetchingSummary && searchSummary.details && searchSummary.details.length > 0 && (
                <div className="filter-list">
                  {searchSummary.details.map((detail, index) => (
                    <div
                      className="filter-option d-flex justify-content-between align-items-center mb-1 p-2 rounded"
                      key={index}
                    >
                      <div>
                        <span className="fw-semibold">{detail.fieldName}</span>
                        <span className="text-muted ms-1">–</span>
                        <span className="ms-1">{detail.foundTerm}</span>
                        <span className="badge bg-light text-secondary ms-2">{detail.count} Result{detail.count !== 1 ? 's' : ''}</span>
                      </div>
                      <Button
                        size="sm"
                        variant="outline-success"
                        onClick={() => applySearchSummaryFilter(detail)}
                      >
                        + Filter
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              {!fetchingSummary && searchSummary.details && searchSummary.details.length === 0 && (
                <div className="text-center text-muted small py-2">
                  <FaSearch className="me-1" />
                  No results found for <span className="fw-bold">"{query}"</span>
                </div>
              )}
            </Popover.Body>
          </Popover>
        )}
      </Overlay>
    </div>
  )
}
